import { type FC, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'wouter';

import { Button, ButtonStyle } from '@atlas/components';
import { findFavoriteGamesRequest } from 'api/casino';
import Loader from 'components/Loader';
import { useCasinoGames } from 'pages/CasinoPage/casino.context';
import { AuthStatus, useClientProfile, useLanguage } from 'store';
import type { CasinoGroup, CasinoSubpage } from 'utils/casino';
import { Translation, useTranslation } from 'utils/translations';
import { LoadingState } from 'utils/types';
import useSliceRequest from 'utils/useSliceRequest';

import FavoriteButton from 'components/FavoriteButton';
import GameTile from 'components/GameTile';

import styles from './GamesList.module.scss';

type FavoritesGamesListProps = {
  slice?: number;
};

const EmptyFavoritesGamesList = () => {
  const label = useTranslation('favorites_no_games');

  return (
    <div className={styles.empty}>
      <img
        src="https://media.pl-test.cdn-platform.xyz/yellow/broken.webp"
        alt="empty favorites games list"
      />
      <p className={styles['empty-text']}>{label}</p>
    </div>
  );
};

const FavoritesGamesList: FC<FavoritesGamesListProps> = ({ slice = 50 }) => {
  const [language] = useLanguage();
  const { setFavoriteGames } = useCasinoGames();
  const [{ sessionToken, status }] = useClientProfile();
  const [, navigate] = useLocation();
  const { group: groupParam } = useParams<{
    group: CasinoGroup;
    subpage: CasinoSubpage;
  }>();

  const requestProps = useMemo(
    () => ({
      slice: { itemsPerSlice: slice },
      query: {
        locale: language.current.code,
      },
      headers: {
        authorization: `Bearer ${sessionToken}`,
      },
    }),
    [slice, sessionToken, language.current.code],
  );

  const { loading, data, hasNext, loadMore } = useSliceRequest(
    findFavoriteGamesRequest,
    requestProps,
  );

  const isEmpty = loading === LoadingState.success && !data.length;
  const isClientResolved = status !== AuthStatus.resolving;
  const isSignedOut = status === AuthStatus.signedOut;

  useEffect(() => {
    if (!isClientResolved) return;

    loadMore();
  }, [loadMore, isClientResolved]);

  useEffect(() => {
    if (data.length) {
      setFavoriteGames(new Set(data.map((game) => game.id)));
    }
  }, [data, setFavoriteGames]);

  useEffect(() => {
    if (isSignedOut) {
      navigate(`/${groupParam}`);
    }
  }, [navigate, isSignedOut, groupParam]);

  if (isEmpty) return <EmptyFavoritesGamesList />;

  return (
    <div className={styles.wrapper}>
      <div className={styles['games-list']}>
        {data.map(({ id, ...props }) => (
          <div key={id} className={styles['game-item']}>
            <GameTile
              id={id}
              favoriteIcon={<FavoriteButton gameId={id} />}
              {...props}
            />
          </div>
        ))}
      </div>
      {loading === LoadingState.loading && <Loader />}
      {hasNext && loading !== LoadingState.loading && (
        <div className={styles['load-more']}>
          <Button style={ButtonStyle.outline} onClick={loadMore}>
            <Translation message="show_more" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default FavoritesGamesList;
